import { useCallback, useContext, useState } from 'react';

import { AnalyticsContext } from 'contexts/analytics';
import { SessionContext } from 'contexts/session';
import Notification from 'components/notification';
import { buildPreflightPayload, runPageExportPreflight } from 'utils/analyticsPageExport/preflight';
import PageExportWarningModal from '../PageExportWarningModal';
import { PREFLIGHT_ERROR_MESSAGES } from './constants';

export type ExportFormat = 'pdf' | 'zip';

interface UsePageExportPreflightArgs {
  onProceed: (format: ExportFormat) => void;
  onThrottled: (retryAfter?: number) => void;
}

interface UsePageExportPreflightReturn {
  startExport: (format: ExportFormat) => Promise<void>;
  warningModal: JSX.Element;
  isPreflighting: boolean;
}

export function usePageExportPreflight({
  onProceed,
  onThrottled,
}: UsePageExportPreflightArgs): UsePageExportPreflightReturn {
  const { filters, currentChart, entitiesTable, tableOrdering } = useContext(AnalyticsContext);
  const session = useContext(SessionContext);

  const [isPreflighting, setIsPreflighting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const buildPayload = useCallback(
    (format: ExportFormat) => {
      const managedTeamIds = (session?.user?.teams ?? [])
        .filter((team) => team.is_manager)
        .map((team) => team.team_id);
      const teamIds = filters?.team_ids
        ? filters.team_ids.split(',').filter(Boolean)
        : session?.isAccountOwner
        ? []
        : managedTeamIds;

      return buildPreflightPayload({
        format,
        accountId: session?.account?.id,
        teamIds,
        dateFrom: filters?.date_from,
        dateTo: filters?.date_to,
        period: filters?.period,
        currentChart,
        currentEntity: entitiesTable?.currentEntity,
        tableOrdering,
      });
    },
    [filters, session, currentChart, entitiesTable, tableOrdering]
  );

  const startExport = useCallback(
    async (format: ExportFormat) => {
      if (isPreflighting) return;
      setIsPreflighting(true);
      try {
        const result = await runPageExportPreflight(buildPayload(format));
        if (result.ok === false) {
          if (result.reason === 'throttled') {
            onThrottled(result.retryAfter);
            return;
          }
          Notification({ type: 'error', text: PREFLIGHT_ERROR_MESSAGES[result.reason] });
          return;
        }
        if (format === 'pdf' && result.data.will_truncate_pdf) {
          setModalOpen(true);
          return;
        }
        onProceed(format);
      } finally {
        setIsPreflighting(false);
      }
    },
    [isPreflighting, buildPayload, onProceed, onThrottled]
  );

  const warningModal = (
    <PageExportWarningModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      onContinuePdf={() => {
        setModalOpen(false);
        onProceed('pdf');
      }}
      onExportZip={() => {
        setModalOpen(false);
        onProceed('zip');
      }}
    />
  );

  return { startExport, warningModal, isPreflighting };
}
